import React, { useState, useRef, useEffect } from 'react';

const DatePicker = ({ value, onChange, placeholder }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const calendarRef = useRef(null);
  
  // Sync inputValue with external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);
  
  // Detect clicks outside the calendar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show/hide calendar
  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  // Handle date selection from calendar
  const handleDateSelect = (date) => {
    const formattedDate = formatDate(date);
    setInputValue(formattedDate);
    onChange(formattedDate);
    setShowCalendar(false);
  };

  // Handle text input changes
  const handleInputChange = (e) => {
    const inputText = e.target.value;
    setInputValue(inputText);
  };

  // Handle input blur (when user leaves the input field)
  const handleInputBlur = () => {
    validateAndUpdateDate(inputValue);
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      validateAndUpdateDate(inputValue);
      e.target.blur(); // Remove focus from input
    }
  };

  // Validate and update date
  const validateAndUpdateDate = (dateString) => {
    if (!dateString.trim()) {
      // Empty input - keep empty state and notify parent
      setInputValue('');
      onChange(''); // 空の値を親コンポーネントに通知
      return;
    }

    const formattedDate = formatInputDate(dateString);
    if (!formattedDate) {
      // Invalid format
      alert('日付の形式が正しくありません。YYYY/MM/DD形式で入力してください。\n例: 2024/04/15');
      setInputValue(value || '');
      return;
    }

    const validationResult = validateDate(formattedDate);
    if (validationResult.isValid) {
      setInputValue(formattedDate);
      onChange(formattedDate);
    } else {
      // Show specific error message
      alert(validationResult.errorMessage);
      setInputValue(value || '');
    }
  };

  // Format input date string to YYYY/MM/DD
  const formatInputDate = (input) => {
    // Remove all non-numeric characters except /
    let cleaned = input.replace(/[^\d/]/g, '');
    
    // Handle various input formats
    if (/^\d{8}$/.test(cleaned)) {
      // YYYYMMDD format
      return `${cleaned.substr(0, 4)}/${cleaned.substr(4, 2)}/${cleaned.substr(6, 2)}`;
    } else if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(cleaned)) {
      // YYYY/M/D or YYYY/MM/DD format
      const parts = cleaned.split('/');
      const year = parts[0];
      const month = parts[1].padStart(2, '0');
      const day = parts[2].padStart(2, '0');
      return `${year}/${month}/${day}`;
    } else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(input)) {
      // YYYY-MM-DD format (convert to YYYY/MM/DD)
      return input.replace(/-/g, '/');
    }
    
    return null;
  };

  // Validate date and return detailed result
  const validateDate = (dateString) => {
    const parts = dateString.split('/');
    if (parts.length !== 3) {
      return {
        isValid: false,
        errorMessage: '日付の形式が正しくありません。YYYY/MM/DD形式で入力してください。\n例: 2024/04/15'
      };
    }
    
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const day = parseInt(parts[2]);
    
    // Year range check
    if (year < 1900 || year > 2100) {
      return {
        isValid: false,
        errorMessage: '年は1900年〜2100年の範囲で入力してください。'
      };
    }
    
    // Basic range checks
    if (month < 1 || month > 12) {
      return {
        isValid: false,
        errorMessage: '月は1〜12の範囲で入力してください。'
      };
    }
    
    if (day < 1 || day > 31) {
      return {
        isValid: false,
        errorMessage: '日は1〜31の範囲で入力してください。'
      };
    }
    
    // Check if the date actually exists
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || 
        date.getMonth() !== month - 1 || 
        date.getDate() !== day) {
      return {
        isValid: false,
        errorMessage: '存在しない日付です。正しい日付を入力してください。'
      };
    }
    
    return { isValid: true };
  };

  // Format date (YYYY/MM/DD)
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  // Convert string date to Date object
  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    
    const parts = dateString.split('/');
    if (parts.length !== 3) return new Date();
    
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };

  // Handle year navigation
  const handleYearChange = (yearOffset) => {
    const currentDate = parseDate(value);
    const newDate = new Date(currentDate.getFullYear() + yearOffset, currentDate.getMonth(), 1);
    const formattedDate = formatDate(newDate);
    setInputValue(formattedDate);
    onChange(formattedDate);
  };

  // Handle month navigation
  const handleMonthChange = (monthOffset) => {
    const currentDate = parseDate(value);
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, 1);
    const formattedDate = formatDate(newDate);
    setInputValue(formattedDate);
    onChange(formattedDate);
  };

  // Render simple calendar
  const renderCalendar = () => {
    const currentDate = parseDate(value);
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // Get first day of the month
    const firstDay = new Date(currentYear, currentMonth, 1);
    // Get last day of the month
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    
    // Day of the week for the first day (0: Sunday, 1: Monday, ..., 6: Saturday)
    const firstDayOfWeek = firstDay.getDay();
    // Number of days in the month
    const daysInMonth = lastDay.getDate();
    
    // Generate week rows
    const weeks = [];
    let days = [];
    
    // Fill in days from previous month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="empty-day"></div>);
    }
    
    // Fill in days for current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isSelected = 
        date.getDate() === currentDate.getDate() && 
        date.getMonth() === currentDate.getMonth() && 
        date.getFullYear() === currentDate.getFullYear();
      
      days.push(
        <div 
          key={`day-${day}`} 
          className={`calendar-day ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateSelect(date)}
        >
          {day}
        </div>
      );
      
      if ((firstDayOfWeek + day) % 7 === 0 || day === daysInMonth) {
        weeks.push(
          <div key={`week-${weeks.length}`} className="calendar-week">
            {days}
          </div>
        );
        days = [];
      }
    }
    
    return (
      <div className="calendar-container" ref={calendarRef}>
        {/* 年移動ナビゲーション */}
        <div className="calendar-year-nav" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <button 
            className="calendar-nav-button year-nav"
            onClick={() => handleYearChange(-1)}
            title="前年"
            style={{ fontSize: '14px', fontWeight: 'bold', marginLeft: '12px' }}
          >
            &lt;&lt;
          </button>
          <span className="year-display" style={{ fontSize: '14px' }}>{currentYear}年</span>
          <button 
            className="calendar-nav-button year-nav"
            onClick={() => handleYearChange(1)}
            title="翌年"
            style={{ fontSize: '14px', fontWeight: 'bold', marginRight: '12px' }}
          >
            &gt;&gt;
          </button>
        </div>
        
        {/* 月移動ナビゲーション */}
        <div className="calendar-header">
          <button 
            className="calendar-nav-button"
            onClick={() => handleMonthChange(-1)}
            title="前月"
            style={{ fontSize: '16px', fontWeight: 'bold' }}
          >
            &lt;
          </button>
          <div className="month-display" style={{ fontWeight: 'bold', fontSize: '16px' }}>{currentMonth + 1}月</div>
          <button 
            className="calendar-nav-button"
            onClick={() => handleMonthChange(1)}
            title="翌月"
            style={{ fontSize: '16px', fontWeight: 'bold' }}
          >
            &gt;
          </button>
        </div>
        
        <div className="calendar-weekdays">
          <div className="weekday sunday">日</div>
          <div className="weekday">月</div>
          <div className="weekday">火</div>
          <div className="weekday">水</div>
          <div className="weekday">木</div>
          <div className="weekday">金</div>
          <div className="weekday saturday">土</div>
        </div>
        <div className="calendar-body">
          {weeks}
        </div>
      </div>
    );
  };

  return (
    <div className="datepicker-container">
      <div className="date-input-wrapper">
        <input 
          type="text" 
          value={inputValue} 
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="date-input"
          title="YYYY/MM/DD形式で入力するか、カレンダーボタンをクリックしてください"
        />
        <button className="calendar-button" onClick={toggleCalendar}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </button>
      </div>
      
      {showCalendar && (
        <div className="calendar-popup">
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};

export default DatePicker;